import { DataGrid, GridColDef, GridFooterContainer } from '@mui/x-data-grid';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { getLogSummary } from '../../services/jira.service';
import { useEffect, useState } from 'react';
import { Box, Button } from '@mui/material';
import { AMD_TELEcOM_BASEURL, AMDTELECOM_ATLASSIAN_URL_WITH_BROWSE, WORK_LOG_PLUGIN } from '../../services/const';
import moment from 'moment';
import { getUserActivityDetails } from '../../services/user.service';

const columns: GridColDef[] = [
    {
        field: 'issue',
        headerName: 'Issue',
        width: 300,
        sortable: false,
        renderCell: (params:any) => {
            return (
                <a href={params?.row?.taskWebUrl} target="_blank" rel='noreferrer'>{params.row.issue}</a>
            )
        },
    },
    {
        field: 'workLog',
        headerName: 'Work logs',
        width: 90,
        sortable: false,
        renderCell: (params:any) => {
            return (
                <span style={{ color: '#1f76ce', fontWeight: '900'}}>{params.row.workLog}</span>
            )
        },
    },
    {
        field: 'originalEstimation',
        headerName: 'Org. est.',
        width: 100,
        sortable: false,
    },
];

const SummaryFooter: React.FC<{ totalTimeSpentSeconds:string, handleViewMonthlyReport: () => void, loading:boolean }> = ({totalTimeSpentSeconds, handleViewMonthlyReport, loading}) => (
    <GridFooterContainer>
      <Box display="flex" justifyContent="space-around" alignItems="center" width="100%">
          <span>Today - <span style={{ color: '#1f76ce', fontWeight: '900'}}>{totalTimeSpentSeconds}</span></span>
          {/* <Button disabled={loading} onClick={handleViewMonthlyReport} variant='outlined'>View Monthly Report</Button> */}
      </Box>
    </GridFooterContainer>
);

export default function LogTable() {
    const projectState = useSelector((state: RootState) => state.project);
    const userState = useSelector((state: RootState) => state.user);
    const [rows, setRows] = useState<any>([]);
    const [loading, setLoading] = useState(false);
    const [totalTimeSpentSeconds, setTotalTimeSpentSeconds] = useState('0m');
    const userDetails = userState?.user;
    const projectTypeMenu = projectState?.projectTypeMenu;
    const projectType = projectState?.projectType;
    const jiraAccountId = userState?.user?.jiraDetails?.accountId || '';

    const formatTimeWithSecond = (seconds: number = 0) =>{
        if(seconds === 0) {
            return '0m';
        }

        const days = Math.floor(seconds / (8 * 3600));
        seconds %= 8 * 3600;
        const hours = Math.floor(seconds / 3600);
        seconds %= 3600;
        const minutes = Math.floor(seconds / 60);
      
        let result = '';
        if (days > 0) result += `${days}d `;
        if (hours > 0) result += `${hours}h `;
        if (minutes > 0) result += `${minutes}m`;
      
        return result.trim();
    }

    const getLogData = async () => {
        setRows([]);
        try {
            setLoading(true);
            setTotalTimeSpentSeconds('0m');
            if (projectType === projectTypeMenu[2]) {
                const jiraAccessToken = localStorage.getItem('jiraAccessToken');
                if ((jiraAccessToken && userDetails && userDetails.jiraDetails && userDetails.jiraDetails?.accountId)) {
                    const accountId = userDetails?.jiraDetails?.accountId || "";
                    const timeZone = userDetails?.jiraDetails?.timeZone || "UTC";
                    const logSummary = await getLogSummary(accountId, timeZone);
                    if(logSummary && logSummary.data && logSummary.data.issues) {
                        setRows(logSummary.data.issues.map((issue: any)=> {
                            return {
                                id: issue.id,
                                issue: `${issue.key} - ${issue.fields.summary}`,
                                workLog: formatTimeWithSecond(issue.dailyTrackerTime),
                                originalEstimation: formatTimeWithSecond(issue.fields.timeoriginalestimate || 0),
                                taskWebUrl: AMDTELECOM_ATLASSIAN_URL_WITH_BROWSE + issue.key,
                                projectKey: issue.fields.project.key || ''
                            }
                        }));
                        const totalTime = logSummary.data.issues.reduce((cumulative: number, curr:any) => {
                            return cumulative + curr.dailyTrackerTime;
                        }, 0);
                        setTotalTimeSpentSeconds(formatTimeWithSecond(totalTime));
                    }
                }
            }
               
            if (projectType === projectTypeMenu[0]) {
              const openProjectLoginDetails = JSON.parse(
                localStorage.getItem("openProjectLoginDetails") ?? "{}"
              );

              if (!openProjectLoginDetails?.userDetails?.login) return;

              const currentDate = moment().format("YYYY-MM-DD");
              const chartId = sessionStorage.getItem("chartId") ?? "";

              const openProjectData = await getUserActivityDetails(
                openProjectLoginDetails.userDetails.login,
                currentDate,
                currentDate,
                "OPEN_PROJECT",
                ["assignee"],
                chartId
              );
              const assigneeTaskData =
                openProjectData?.data?.[0]?.activity?.assigneeTask?.[
                  currentDate
                ]?.openProject;
              if (assigneeTaskData?.totalWorkLogsInfo?.length) {
                const workLogs = assigneeTaskData.totalWorkLogsInfo.map(
                  (issue: any) => {
                    const totalTimeSpent =
                      moment.duration(issue.totalTimeSpentToday).asSeconds() ||
                      0;
                    const originalEstimate =
                      moment.duration(issue.originalEstimate).asSeconds() || 0;
                    return {
                      id: issue?.issueKey,
                      issue: `${issue?.issueKey} - ${issue?.issueSummary}`,
                      workLog: formatTimeWithSecond(totalTimeSpent),
                      originalEstimation:
                        formatTimeWithSecond(originalEstimate),
                      taskWebUrl: issue?.issueLink ?? "",
                      projectKey: issue?.projectDetails?.key ?? "",
                    };
                  }
                );
                setRows(workLogs);
                const totalTimeSpentOnDay =
                  assigneeTaskData?.totalTimeSpentOnDay ?? 0;
                setTotalTimeSpentSeconds(
                  formatTimeWithSecond(totalTimeSpentOnDay)
                );
              }
            }
        } catch {
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    const startOfMonth = () => {
        const now = new Date();
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
        return startOfMonth;
    }

    const endOfMonth = () => {
        const now = new Date();
        const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999).getTime();
        return endOfMonth;
    }

    const handleViewMonthlyReport = () => {
        if(jiraAccountId) {
            const projectKeys = Array.from( new Set(rows.map((e: any) => e?.projectKey) || []) );
            window.open(`${AMD_TELEcOM_BASEURL}projects/ALW?selectedItem=${WORK_LOG_PLUGIN}userIds=${jiraAccountId}&projects=${projectKeys.join(',')}&startDate=${startOfMonth()}&endDate=${endOfMonth()}&period=day&sortIndex=0&sortAsc=true`, '_blank');
        }
    }

    const slotComponents: any = {
        footer: SummaryFooter,
    };
    
    const slotProps: any = {
        footer: { totalTimeSpentSeconds, handleViewMonthlyReport, loading },
    };

    useEffect(() => {
        getLogData();
    }, []);

    return (
        <div style={{ height: 400, width: 500 }}>
            <DataGrid
                className="issue-table"
                loading={loading}
                rows={rows}
                columns={columns}
                disableColumnMenu
                slots={slotComponents}
                slotProps={slotProps}
            />
        </div>
    );
}